import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { PopupModal } from "../../components/Modal";
import { Page } from "../../components/Page";
import { ShowToast } from "../../components/toast";
import { api } from "../../helpers/api";

// =============================================================================
// TYPES
// =============================================================================

interface CsCustomerEmail {
  id: string;
  email: string;
  createdAt: string;
}

interface CustomerNote {
  id: string;
  emailIdentifier: string;
  userId: string | null;
  userName: string | null;
  hotelName: string | null;
  note: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  emails: CsCustomerEmail[];
}

interface UserSearchResult {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  hotelName: string | null;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/** Standalone content (no Page wrapper) — used inside CustomerSuccess tabs */
export function CustomerNotesContent() {
  return <CustomerNotesInner />;
}

/** Full page with Page wrapper — standalone route (legacy) */
export function CustomerNotes() {
  return (
    <Page>
      <CustomerNotesInner />
    </Page>
  );
}

function CustomerNotesInner() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingNote, setEditingNote] = useState<CustomerNote | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["customer-notes", debouncedSearch, page],
    queryFn: () =>
      api.customerNotes.list({
        search: debouncedSearch || undefined,
        page,
        limit: 50,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.customerNotes.delete(id),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Note deleted" });
      setDeleteConfirmId(null);
      queryClient.invalidateQueries({ queryKey: ["customer-notes"] });
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to delete note" }),
  });

  const notes: CustomerNote[] = data?.data || [];
  const total: number = data?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const columnHelper = createColumnHelper<CustomerNote>();

  const columns = [
    columnHelper.accessor("userName", {
      header: "Customer",
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>
            <span className="text-sm font-medium text-content-primary">
              {row.userName || row.emailIdentifier}
            </span>
            {row.hotelName && (
              <p className="text-xs text-content-tertiary mt-0.5">
                {row.hotelName}
              </p>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("emailIdentifier", {
      header: "Email",
      cell: (info) => (
        <span className="text-sm text-content-secondary">
          {info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("note", {
      header: "Note",
      cell: (info) => (
        <p className="text-sm text-gray-600 line-clamp-2 max-w-[300px]">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("updatedAt", {
      header: "Updated",
      cell: (info) => (
        <span className="text-sm text-content-tertiary whitespace-nowrap">
          {new Date(info.getValue()).toLocaleString("en-US", {
            timeZone: "America/New_York",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })}
        </span>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditingNote(info.row.original);
            }}
            className="px-3 py-1.5 text-xs font-medium text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover"
          >
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirmId(info.row.original.id);
            }}
            className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100"
          >
            Delete
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable<CustomerNote>({
    data: notes,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex-1">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <h1 className="text-2xl font-semibold text-content-primary">
          Customer Profiles
        </h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:opacity-90"
        >
          + Add Customer
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex px-3 py-2 border border-[#E9E9EB] bg-surface-primary rounded-lg items-center gap-2 min-w-[240px]">
          <SearchIcon className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by email or note..."
            className="w-full outline-none text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-primary rounded-lg shadow-sm border border-border-secondary">
        <div className="overflow-auto max-h-[calc(100vh-320px)]">
          <table className="w-full">
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="sticky top-0 z-10">
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={clsx(
                        "text-[0.875rem] font-medium text-[#202020] text-start px-4 py-3 bg-[#F4F4F6]",
                        index === headerGroup.headers.length - 1 &&
                          "rounded-tr-[8px] rounded-br-[8px]",
                        index === 0 && "rounded-tl-[8px] rounded-bl-[8px]",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {!isLoading &&
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="cursor-pointer hover:bg-surface-hover"
                    onClick={() => setEditingNote(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-3 border-b border-b-border-primary font-medium text-[0.8125rem]"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              {(isLoading || isFetching) && (
                <TableSkeleton rows={5} columns={5} />
              )}
            </tbody>
          </table>

          {!isLoading && !isFetching && notes.length === 0 && (
            <div className="flex flex-col justify-center items-center py-16">
              <span className="text-content-tertiary text-[1rem] font-medium">
                No customer notes yet
              </span>
              <span className="text-[#bbbbbb] text-[0.875rem] mt-1">
                Add a customer to pre-load context for AI responses
              </span>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-secondary">
            <span className="text-sm text-content-tertiary">
              {total} note{total !== 1 ? "s" : ""} total
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-content-secondary">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <NoteModal
          onClose={() => setShowAddModal(false)}
          onSaved={() => {
            setShowAddModal(false);
            queryClient.invalidateQueries({ queryKey: ["customer-notes"] });
          }}
        />
      )}

      {/* Edit Modal */}
      {editingNote && (
        <NoteModal
          note={editingNote}
          onClose={() => setEditingNote(null)}
          onSaved={() => {
            setEditingNote(null);
            queryClient.invalidateQueries({ queryKey: ["customer-notes"] });
          }}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <PopupModal
          onClose={() => setDeleteConfirmId(null)}
          contentClassName="mx-auto max-w-[400px] w-full"
        >
          <h3 className="text-lg font-semibold text-gray-900">
            Delete Customer Note
          </h3>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete this customer note and all
            associated emails? This cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setDeleteConfirmId(null)}
              className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteMutation.mutate(deleteConfirmId)}
              disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </button>
          </div>
        </PopupModal>
      )}
    </div>
  );
}

// =============================================================================
// USER SEARCH DROPDOWN
// =============================================================================

function UserSearchDropdown({
  onSelect,
  selectedUser,
  onClear,
}: {
  onSelect: (user: UserSearchResult) => void;
  selectedUser: UserSearchResult | null;
  onClear: () => void;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: users, isLoading } = useQuery({
    queryKey: ["user-search", debouncedQuery],
    queryFn: () => api.customerNotes.searchUsers(debouncedQuery, 15),
    enabled: debouncedQuery.length >= 2,
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (selectedUser) {
    return (
      <div className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
        <div>
          <span className="text-sm font-medium text-gray-900">
            {selectedUser.firstName} {selectedUser.lastName}
          </span>
          <span className="text-sm text-gray-500 ml-2">
            ({selectedUser.email})
          </span>
          {selectedUser.hotelName && (
            <p className="text-xs text-gray-500">{selectedUser.hotelName}</p>
          )}
        </div>
        <button
          onClick={onClear}
          className="text-xs text-red-500 hover:text-red-700 ml-2"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => {
          if (query.length >= 2) setIsOpen(true);
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        placeholder="Search by name, email, or hotel..."
      />

      {isOpen && debouncedQuery.length >= 2 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[240px] overflow-auto">
          {isLoading && (
            <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
          )}
          {!isLoading && users?.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              No users found
            </div>
          )}
          {!isLoading &&
            users?.map((user: UserSearchResult) => (
              <button
                key={user.id}
                onClick={() => {
                  onSelect(user);
                  setIsOpen(false);
                  setQuery("");
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
              >
                <div className="text-sm font-medium text-gray-900">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-xs text-gray-500">
                  {user.email}
                  {user.hotelName && ` — ${user.hotelName}`}
                </div>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// MULTI-EMAIL INPUT
// =============================================================================

function MultiEmailInput({
  emails,
  onChange,
}: {
  emails: string[];
  onChange: (emails: string[]) => void;
}) {
  const [inputValue, setInputValue] = useState("");

  const addEmail = () => {
    const email = inputValue.trim().toLowerCase();
    if (email && !emails.includes(email) && email.includes("@")) {
      onChange([...emails, email]);
      setInputValue("");
    }
  };

  const removeEmail = (email: string) => {
    onChange(emails.filter((e) => e !== email));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {emails.map((email) => (
          <span
            key={email}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200"
          >
            {email}
            <button
              onClick={() => removeEmail(email)}
              className="text-blue-400 hover:text-blue-600 ml-0.5"
            >
              x
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="email"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addEmail();
            }
          }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Add email address..."
        />
        <button
          onClick={addEmail}
          disabled={!inputValue.trim() || !inputValue.includes("@")}
          className="px-3 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
      <p className="text-xs text-gray-400 mt-1">
        Press Enter or click Add. These emails will be matched when tickets come
        in.
      </p>
    </div>
  );
}

// =============================================================================
// NOTE MODAL (Add / Edit)
// =============================================================================

function NoteModal({
  note,
  onClose,
  onSaved,
}: {
  note?: CustomerNote;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEditing = !!note;

  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(
    note?.userId
      ? {
          id: note.userId,
          email: note.emailIdentifier || "",
          firstName: note.userName?.split(" ")[0] || "",
          lastName: note.userName?.split(" ").slice(1).join(" ") || "",
          hotelName: note.hotelName || null,
        }
      : null,
  );
  const [emails, setEmails] = useState<string[]>(
    note?.emails?.map((e) => e.email) ||
      (note?.emailIdentifier ? [note.emailIdentifier] : []),
  );
  const [noteText, setNoteText] = useState(note?.note || "");

  const handleUserSelect = (user: UserSearchResult) => {
    setSelectedUser(user);
    // Auto-add user's primary email if not already in the list
    if (!emails.includes(user.email.toLowerCase())) {
      setEmails((prev) => [...prev, user.email.toLowerCase()]);
    }
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api.customerNotes.create({
        userId: selectedUser?.id,
        userName: selectedUser
          ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim()
          : undefined,
        hotelName: selectedUser?.hotelName || undefined,
        note: noteText,
        emails,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Customer note created" });
      onSaved();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to create note" }),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      api.customerNotes.update(note!.id, {
        userId: selectedUser?.id,
        userName: selectedUser
          ? `${selectedUser.firstName} ${selectedUser.lastName}`.trim()
          : undefined,
        hotelName: selectedUser?.hotelName || undefined,
        note: noteText,
        emails,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Customer note updated" });
      onSaved();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to update note" }),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;
  const canSave = emails.length > 0 && noteText.trim().length > 0;

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="mx-auto max-w-[560px] w-full"
    >
      <h3 className="text-lg font-semibold text-content-primary">
        {isEditing ? "Edit Customer Note" : "Add Customer Note"}
      </h3>

      <div className="space-y-4">
        {/* User Picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Link to User{" "}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <UserSearchDropdown
            onSelect={handleUserSelect}
            selectedUser={selectedUser}
            onClear={() => setSelectedUser(null)}
          />
          <p className="text-xs text-gray-400 mt-1">
            Search and select an Autumn user. Their primary email will be
            auto-added.
          </p>
        </div>

        {/* Email Addresses */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Addresses
          </label>
          <MultiEmailInput emails={emails} onChange={setEmails} />
        </div>

        {/* Context Note */}
        <div>
          <label className="block text-sm font-medium text-content-primary mb-1">
            Context Note
          </label>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="w-full h-[120px] px-3 py-2 border border-border-primary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary resize-y"
            placeholder="e.g., High-value customer, 45 rooms, been with us 8 months. Compared us to Duetto last month. Prefers direct, data-driven communication."
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover"
        >
          Cancel
        </button>
        <button
          onClick={() =>
            isEditing ? updateMutation.mutate() : createMutation.mutate()
          }
          disabled={!canSave || isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:opacity-90 disabled:opacity-50"
        >
          {isPending ? "Saving..." : isEditing ? "Update Note" : "Add Customer"}
        </button>
      </div>
    </PopupModal>
  );
}

// =============================================================================
// TABLE SKELETON
// =============================================================================

function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className="px-4 py-4 border-b border-b-border-primary"
            >
              <div className="w-full h-4 bg-surface-disabled rounded-md"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
